import React from 'react'
import clsx from 'clsx'
import { Paper, Typography, Box, makeStyles } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import Tooltip from '../../../../../control/tooltip/tooltip'
import { SummaryItem } from './DashBoardType'
import useTranslate from '../../../../../i18n/useTranslate'

type SummaryCardProps = {
    title: string
    items: SummaryItem[]
    canFilter?: boolean
    onClickLabel?: (item: SummaryItem) => void
    onClickProcessingText?: (item: SummaryItem) => void
    onClickOverdueText?: (item: SummaryItem) => void
}

const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    items,
    onClickLabel,
    onClickProcessingText,
    onClickOverdueText,
    canFilter = true,
}) => {
    const classes = useStyles()
    const translate = useTranslate()

    return (
        <Paper className={classes.card} elevation={0}>
            <Typography variant="h6" className={classes.title}>
                {title}
            </Typography>
            <Box className={classes.content}>
                {items.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" className={classes.empty}>
                        {translate.get('LABEL.NO_DATA_FOUND')}
                    </Typography>
                ) : (
                    items.map((item) => {
                        const hasValue = typeof item.value === 'number' || typeof item.value === 'string'

                        return (
                            <Box key={item.key} className={classes.row}>
                                <Box className={classes.iconBox} style={{ background: item.color }}>
                                    <DescriptionIcon style={{ width: 35, height: 35 }} />
                                </Box>

                                <Box className={classes.labelWrapper}>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.label, { [classes.clickable]: canFilter })}
                                        onClick={() => onClickLabel?.(item)}
                                    >
                                        {item.name ?? '-'}
                                    </Typography>

                                    <Box display="flex" alignItems="center">
                                        {hasValue && (
                                            <Typography
                                                variant="body1"
                                                className={clsx(classes.value, { [classes.clickable]: canFilter })}
                                                onClick={canFilter ? () => onClickProcessingText?.(item) : undefined}
                                            >
                                                {typeof item.value === 'number'
                                                    ? item.value.toLocaleString()
                                                    : item.value}
                                            </Typography>
                                        )}

                                        {typeof item.star === 'number' && (
                                            <Tooltip
                                                title={translate.get('UM.PROJECT_DASHBOARD.OVERDUE')}
                                                placement="top"
                                            >
                                                <span>
                                                    <Typography
                                                        variant="body1"
                                                        component="span"
                                                        className={clsx(classes.star, {
                                                            [classes.clickable]: canFilter,
                                                        })}
                                                        onClick={() => onClickOverdueText?.(item)}
                                                    >
                                                        {hasValue ? ` (${item.star}*)` : `${item.star}*`}
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )
                    })
                )}
            </Box>
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        borderRadius: 16,
        padding: theme.spacing(2),
        boxShadow: 'none',
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
    },
    title: {
        textAlign: 'center',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        flex: 1,
    },
    iconBox: {
        width: 60,
        height: 60,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1.5),
        flexShrink: 0,
        color: '#fff',
    },
    label: { display: 'flex', justifyContent: 'space-between', gap: 8 },
    value: { fontWeight: 700 },
    overdue: { fontWeight: 700, color: '#D62E4B' },
    star: { marginLeft: 4, color: '#D62E4B', fontWeight: 700 },
    empty: { paddingBlock: 48, marginInline: 'auto' },
    clickable: { cursor: 'pointer' },
    labelWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
    },
}))

export default SummaryCard
